<script lang="ts">
	import { t } from '$i18n/i18n'
	import TextArea from '#lib/atoms/TextArea.svelte'
	import InputField from '#lib/molecules/InputField.svelte'
	import type { FontProps } from '$types/fontProps'
	import { DefaultRobotoFontProps } from '$types/fontProps'
	import Modal from '#lib/organisms/Modal.svelte'
	import { modals } from 'svelte-modals'
	import type { Material } from './types/material'
	import { materialSchema } from './types/material'
	import type { QuantityUnit } from './types/quantity'
	import type { AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { AutocompleteConfig } from '$types/autocompleteFieldConfig'
	import AutocompleteInputField from '#lib/autocomplete/AutocompleteInputField.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import { addNotification } from '#lib/stores.js'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { getContext, hasContext, onMount, untrack } from 'svelte'
	import MaterialPropertyCard from './MaterialPropertyCard.svelte'
	import type { MaterialProperty } from './types/materialProperty'
	import { DefaultMaterialProperty } from './types/materialProperty'
	import { slide } from 'svelte/transition'
	import { getFormErrors } from '$utils/formValidation'
	import { writable, type Writable } from 'svelte/store'
	import MaterialPropertySelection from './MaterialPropertySelection.svelte'
	import MaterialPropertyModal from './MaterialPropertyModal.svelte'
	import Checkbox from '#lib/atoms/Checkbox.svelte'
	import { checkUnedited } from '$utils/misc'
	import IconCheckmark from '$svg/icon-checkmark.svg?component'
	import ArrowLeft from '$svg/icon-arrow-left.svg?component'
	import IconCross from '$svg/icon-cross.svg?component'
	import IconTrash from '$svg/icon-trash.svg?component'
	import IconPlus from '$svg/icon-plus.svg?component'
	import { deepEqual } from '$utils/utils.svelte'
	import Button from '#lib/atoms/Button.svelte'
	import { mmpvState } from './mmpv.state.svelte'
	import type { Catalog } from './types/catalog'
	import { getSortedPropertiesOfMaterial } from './mmpv-properties.controller.svelte'
	import {
		deleteMaterial,
		saveOrUpdateMaterial,
		removeMaterialProperty
	} from './mmpv-materials.controller.svelte'
	import type { FormError } from '$types/formError.ts'

	const fontProps: FontProps = DefaultRobotoFontProps('0.875rem', '0.75rem')
	const headerFontProps: FontProps = DefaultRobotoFontProps('1.125rem', '1rem')
	const MATERIAL_FORM_ERROR_PATH_PREFIX = 'material.'

	type MaterialDetailsProps = {
		materialCopy?: Material
		isNew?: boolean
		showEditProperty?: boolean
		addRoot: () => void
		backToCatalogs: () => void
		changeMaterial: (material: Material) => void
		removeNode: (material: Material) => void
	}

	let {
		materialCopy = $bindable(),
		isNew = false,
		showEditProperty = $bindable(false),
		addRoot,
		backToCatalogs,
		changeMaterial,
		removeNode
	}: MaterialDetailsProps = $props()

	let editedProperty: MaterialProperty = $state(DefaultMaterialProperty())

	let material: Material = $derived(mmpvState.selectedMaterial)
	let catalog: Catalog = $derived(mmpvState.selectedCatalog)
	let properties: MaterialProperty[] = $derived([...(material.properties ?? [])])

	let materialNameForHeader: string = $derived(
		`${material?.parentCode ?? ''}${material?.code}${material?.highlighted ? catalog?.highlightedValue : ''} ${material?.name}`
	)

	let disableAddNewPropertyBtn: boolean = $derived(
		!editedProperty.id || editedProperty.id === 'new'
	)

	type QuantityOption = { publicId: string; displayLabel: string }

	const quantityAcConfig: AutocompleteFieldConfig = AutocompleteConfig('publicId', 'displayLabel')
	quantityAcConfig.valueAsObject = true

	let quantityOptions: QuantityOption[] = $derived(
		mmpvState.quantities.map((unit: QuantityUnit) => ({
			publicId: unit.publicId,
			displayLabel: `${unit.label} (${unit.unit})`
		}))
	)

	/**
	 * mirrors material.quantityId (the source of truth) into a derived svelecte overrides on
	 * selection. valueAsObject means the AC value is the whole option, not the id, so
	 * writeSelectedQuantityToMaterial writes the id back. See MaterialPropertySelection's `acProperty`.
	 */
	let acQuantity: QuantityOption | undefined = $derived(
		quantityOptions.find((option) => option.publicId === material.quantityId)
	)

	/**
	 * `?? null`: a cleared quantity must persist as null, not undefined — JSON.stringify drops
	 * undefined on save, so the backend keeps the old assignment and the deletion is lost.
	 */
	function writeSelectedQuantityToMaterial() {
		material.quantityId = acQuantity?.publicId ?? null
	}

	let formErrors: Writable<FormError[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])

	$effect(() => {
		if (mmpvState.materialProperties) {
			/**
			 * untracked: updateMaterialProperties() writes selectedMaterial, so tracking it here
			 * would re-trigger this effect on its own write (effect_update_depth_exceeded).
			 */
			untrack(() => {
				if (properties) {
					updateMaterialProperties()
					/**
					 * materialCopy was cloned before properties loaded, so its .properties is stale —
					 * sync it so the deepEqual dirty-check doesn't open the unsaved-changes modal on
					 * every node click.
					 */
					materialCopy = $state.snapshot(material)
				}
			})
		}
	})

	onMount(() => {
		// needed by first load of mmpvState page with material in url to show and update properties
		if (material.propertyIds) {
			changeMaterial(material)
		}
	})

	function handleDelete() {
		modals.open(Modal, {
			title: $t('UI.mmpv.delete.modal.title', { entity: material.name }),
			message: !(material.childrenIds === undefined || material.childrenIds.length === 0)
				? $t('UI.mmpv.delete.not.possible.modal.message', { material: material.name })
				: $t('UI.mmpv.delete.modal.message', { material: material.name }),
			denyText: $t('UI.delete'),
			denyDisabled: !(material.childrenIds === undefined || material.childrenIds.length === 0),
			onDeny: () => {
				deleteMaterial(material, catalog, { addRoot, changeMaterial })
				modals.close()
			}
		})
	}

	function handleRemoveEmpty() {
		let isMaterialUnedited: boolean = checkUnedited(material, materialCopy)
		if (isMaterialUnedited) {
			removeNode(material)
		} else {
			modals.open(Modal, {
				title: $t('UI.modal.unsavedChanges.header'),
				message: $t('UI.mmpv.material.delete.modal.message'),
				denyText: $t('UI.mmpv.material.delete.modal.button'),
				onDeny: () => {
					removeNode(material)
					modals.close()
				}
			})
		}
	}

	async function addPropertyToMaterial(propertyId: string) {
		if (material.propertyIds === undefined) {
			material.propertyIds = []
		}
		let propertyToAdd = mmpvState.getMaterialProperty(propertyId)
		if (propertyToAdd) {
			propertyToAdd = { ...propertyToAdd, isEditable: true }
			let propAsList = [propertyToAdd]
			mmpvState.selectedMaterial = {
				...material,
				propertyIds: [...material.propertyIds, propertyId],
				properties: [...(material?.properties ?? ([] as MaterialProperty[])), ...propAsList]
			}
		}
		showPropertyList()
	}

	function updateMaterialProperties() {
		let parentProperties = properties.filter((prop: MaterialProperty) => !prop.isEditable)
		let propsToAdd: MaterialProperty[] = []
		if (material.propertyIds === undefined) {
			material.propertyIds = []
		}
		material.propertyIds.forEach((propId: string) => {
			const materialProperty = mmpvState.getMaterialProperty(propId)
			if (materialProperty) {
				propsToAdd.push({ ...materialProperty, isEditable: true })
			}
		})
		parentProperties.forEach((prop: MaterialProperty) => propsToAdd.push(prop))

		mmpvState.selectedMaterial = {
			...material,
			properties: [...propsToAdd]
		}
	}

	async function saveMaterialAndGoToCatalog() {
		try {
			// persist only the material's own (editable) properties — inherited/parent
			// properties are recomputed on load and must not be saved onto the material
			const editableProperties = (material.properties ?? []).filter(
				(property: MaterialProperty) => property.isEditable
			)
			const materialToSave: Material = { ...material, properties: editableProperties }
			const validatedMaterial = await materialSchema.validate(materialToSave, {
				stripUnknown: false,
				abortEarly: false
			})
			const updatedMaterial = await saveOrUpdateMaterial(validatedMaterial, catalog)
			if (updatedMaterial) {
				addNotification(
					DefaultNotification($t('UI.mmpv.material.saved.notification'), NotificationType.SUCCESS)
				)
				changeMaterial(updatedMaterial)
			}
		} catch (error) {
			const err = getFormErrors(error, MATERIAL_FORM_ERROR_PATH_PREFIX)
			console.warn('ValidationError: ', err)
			$formErrors = err
		}
	}

	function handleBackToCatalogs() {
		let isMaterialUnedited: boolean = true
		if (materialCopy) {
			materialCopy.properties = getSortedPropertiesOfMaterial(materialCopy)
			material.properties = getSortedPropertiesOfMaterial(material)
			isMaterialUnedited = deepEqual(material, materialCopy)
		}
		if (isMaterialUnedited) {
			backToCatalogs()
		} else {
			modals.open(Modal, {
				title: $t('UI.modal.unsavedChanges.header'),
				message: $t('UI.mmpv.modal.material.unsavedChanges.message'),
				denyText: $t('UI.saveAndDiscardChanges'),
				onDeny: () => {
					mmpvState.selectedMaterial = materialCopy
					backToCatalogs()
					modals.close()
				}
			})
		}
	}

	function showPropertyList() {
		editedProperty = DefaultMaterialProperty()
		showEditProperty = false
	}

	function editProperty(materialProperty: MaterialProperty | null) {
		if (materialProperty) {
			editedProperty = { ...materialProperty }
		}
		showEditProperty = true
	}

	function updateMaterialProperty(materialPropertyId: string) {
		const updatedMaterialProperty = mmpvState.getMaterialProperty(materialPropertyId)
		if (updatedMaterialProperty) {
			const idx = properties.findIndex((prop) => prop.id === materialPropertyId)
			const idxCopy =
				materialCopy?.properties.findIndex((prop) => prop.id === materialPropertyId) ?? -1
			if (idx > -1 && idxCopy > -1) {
				const updatedProperties = [...properties]
				updatedProperties[idx] = updatedMaterialProperty
				mmpvState.selectedMaterial = { ...material, properties: updatedProperties }
				if (materialCopy) {
					materialCopy.properties[idxCopy] = updatedMaterialProperty
				}
			}
		}
	}

	async function removeProperty(propertyToRemove: MaterialProperty) {
		const success = await removeMaterialProperty(material, catalog, propertyToRemove)
		if (success) {
			mmpvState.selectedMaterial = {
				...material,
				properties: properties.filter((prop) => prop.id !== propertyToRemove.id),
				propertyIds: (material.propertyIds ?? []).filter((id) => id !== propertyToRemove.id)
			}
		}
	}

	function createNewProperty() {
		modals.open(MaterialPropertyModal, {
			materialProperty: DefaultMaterialProperty(),
			materialId: material.id,
			updateMaterial: (propertyId: string) => {
				addPropertyToMaterial(propertyId)
			},
			parent: material,
			isNew: true
		})
	}
</script>

<div class="material-details-container">
	<div class="material-details-header">
		<span
			>{isNew
				? $t('UI.mmpv.materialDetails.header.new', { material: materialNameForHeader })
				: $t('UI.mmpv.materialDetails.header.edit', { material: materialNameForHeader })}</span
		>
		{#if isNew}
			<Button variant="secondary-icon" title={$t('UI.button.cancel')} onclick={handleRemoveEmpty}>
				<IconCross width={24} height={24} fill="black" />
			</Button>
		{:else}
			<Button color="red" variant="secondary-icon" title={$t('UI.delete')} onclick={handleDelete}>
				<IconTrash width={24} height={24} fill="black" />
			</Button>
		{/if}
	</div>
	<div class="material-details-body">
		<div>
			<InputField
				id="code_prefix"
				value={material.parentCode}
				{fontProps}
				{headerFontProps}
				label={$t('UI.mmpv.material.code.prefix')}
				format={inputFieldFormats.FULL}
				errorPath={MATERIAL_FORM_ERROR_PATH_PREFIX + 'parentCode'}
				readOnly
			/>
			<InputField
				bind:value={material.code}
				id="code_suffix"
				{fontProps}
				{headerFontProps}
				placeholder="01"
				label="Code-Suffix"
				readOnly={!isNew}
				format={inputFieldFormats.FULL}
				errorPath={MATERIAL_FORM_ERROR_PATH_PREFIX + 'code'}
				required
			/>
		</div>
		<div>
			<InputField
				bind:value={material.name}
				id="material_name"
				{fontProps}
				placeholder={$t('UI.mmpv.material.name.ph')}
				label={isNew
					? $t('UI.mmpv.materialName.header.newMaterial')
					: $t('UI.mmpv.materialName.header')}
				format={inputFieldFormats.FULL}
				{headerFontProps}
				errorPath={MATERIAL_FORM_ERROR_PATH_PREFIX + 'name'}
				required
			/>
			<Checkbox
				bind:checked={material.highlighted}
				id="material_highlighted"
				label={$t('UI.mmpv.material.highlighted.label')}
			/>
		</div>
		<div style="width: 100%;">
			{#key material.id}
				<AutocompleteInputField
					bind:value={acQuantity}
					options={quantityOptions}
					{fontProps}
					{headerFontProps}
					autocompleteConfig={quantityAcConfig}
					label={$t('UI.mmpv.material.quantity.label')}
					hideDropdownArrow={false}
					onChange={writeSelectedQuantityToMaterial}
				/>
			{/key}
		</div>
		<div>
			<TextArea
				bind:text={material.description}
				id="material_description"
				title={$t('UI.mmpv.material.description')}
				placeholder={$t('UI.writeHere.ph')}
				marginTop="0"
				labelGap="0.375rem"
				inputFontProps={fontProps}
				{headerFontProps}
				errorPath={MATERIAL_FORM_ERROR_PATH_PREFIX + 'description'}
			/>
			<TextArea
				id="material_internalRemarks"
				bind:text={material.comment}
				title={$t('UI.mmpv.material.internalRemarks')}
				placeholder={$t('UI.writeHere.ph')}
				marginTop="0"
				labelGap="0.375rem"
				inputFontProps={fontProps}
				{headerFontProps}
				errorPath={MATERIAL_FORM_ERROR_PATH_PREFIX + 'internalRemarks'}
			/>
		</div>
	</div>
	<div style="width: 100%">
		{#if showEditProperty}
			<div class="propertySelection" transition:slide={{ duration: 300 }}>
				<div class="material-property-create-row">
					<MaterialPropertySelection
						bind:materialProperty={editedProperty}
						alreadyMappedProperties={properties}
					/>

					<Button
						variant="primary"
						disabled={disableAddNewPropertyBtn}
						onclick={() => addPropertyToMaterial(editedProperty.id)}
					>
						<IconPlus width={20} height={20} fill="inherit" />
						<span style="white-space: nowrap; color: inherit"
							>{$t('UI.mmpv.property.addToMaterial')}</span
						>
					</Button>
				</div>
				<div class="property-buttons-row">
					<Button
						variant="secondary"
						color="grey"
						title={$t('UI.button.cancel')}
						onclick={showPropertyList}
					>
						{$t('UI.button.cancel')}
					</Button>
					<Button
						variant="secondary"
						title={$t('UI.mmpv.materialProperty.createNew')}
						onclick={createNewProperty}
					>
						{$t('UI.mmpv.materialProperty.createNew')}
					</Button>
				</div>
			</div>
		{:else}
			<div style="width: 100%" transition:slide={{ duration: 300 }}>
				<div class="material-properties">
					<div>
						<span>{$t('UI.mmpv.material.selectedProperties')}</span>
						<Button
							variant="secondary-icon"
							title={$t('UI.mmpv.property.addToMaterial')}
							onclick={() => editProperty(null)}
						>
							<IconPlus width={32} height={32} fill="black" />
						</Button>
					</div>
					<div>
						{#if properties && properties.length > 0}
							{const sortedProperties = $derived(getSortedPropertiesOfMaterial(material))}
							{#each sortedProperties as prop, idx (prop.id)}
								<MaterialPropertyCard
									property={sortedProperties[idx]}
									{idx}
									isEditable={prop.isEditable}
									onRemoveProperty={removeProperty}
									onUpdateMaterialProperty={updateMaterialProperty}
								/>
							{/each}
						{/if}
					</div>
				</div>
				<div class="buttons-row">
					<Button
						variant="secondary"
						title={$t('UI.mmpv.backToCatalogs')}
						onclick={handleBackToCatalogs}
					>
						<ArrowLeft width={20} height={20} fill="inherit" />
						{$t('UI.mmpv.backToCatalogs')}
					</Button>
					<Button
						id="btn-create-new-material"
						variant="primary"
						onclick={saveMaterialAndGoToCatalog}
					>
						<IconCheckmark width={20} height={20} fill="inherit" />
						{isNew ? $t('UI.mmpv.createMaterial') : $t('UI.button.save')}
					</Button>
				</div>
			</div>
		{/if}
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.material-details-container {
		width: 100%;
		max-width: 1000px; /* TODO remove later */
		position: sticky;
		top: 3rem;

		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 1.88rem;

		& > .material-details-header {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;

			width: 100%;

			& > span {
				font-family: Roboto, sans-serif;
				line-height: 24px;
				font-weight: 400;
				font-size: 1.25rem;
				color: black;

				width: 90%;
				user-select: text;
			}
		}

		& > .material-details-body {
			width: 100%;

			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 1.88rem;

			& > div {
				width: 100%;

				display: flex;
				flex-direction: row;
				align-items: flex-end;
				justify-content: center;
				gap: 1.88rem;
			}
		}
	}

	div.propertySelection {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 1.5rem;

		width: 100%;
	}

	.buttons-row {
		width: 100%;

		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		gap: 14px;

		margin-top: 20px;
	}

	.property-buttons-row {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-end;
		gap: 6px;

		width: 100%;
	}

	.material-properties {
		width: 100%;

		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 20px;

		& > div:first-child {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;

			width: 100%;

			& > span:first-child {
				font-family: Roboto, sans-serif;
				line-height: 19px;
				font-weight: 400;
				font-size: 1rem;
				color: black;
			}
		}

		& > div:last-child {
			width: 100%;
			display: grid;
			grid-template-columns: repeat(auto-fill, 160px);
			grid-template-rows: repeat(auto-fill, 66px);
			gap: 0.875rem 0.875rem;
			min-height: fit-content;
			margin-bottom: 20px;
		}
	}

	div.material-property-create-row {
		display: flex;
		flex-direction: row;
		align-items: flex-end;
		justify-content: center;
		gap: 6px;

		width: 100%;
	}
</style>
